import { buildDispatchFallback } from "./gemini";

const DEFAULT_BACKEND_URL = "http://localhost:8000";

export function getBackendUrl() {
  return (process.env.REACT_APP_BACKEND_URL || DEFAULT_BACKEND_URL).replace(/\/$/, "");
}

/** Map a Firestore / demo packet into the backend TriagePacket contract. */
export function webPacketToTriagePacket(packet) {
  const lng = packet.lng ?? packet.lon;
  return {
    packet_id: String(packet.id ?? `pkt-${Date.now()}`),
    timestamp: new Date(packet.createdAt ?? Date.now()).toISOString(),
    location:
      packet.lat != null && lng != null ? { lat: Number(packet.lat), lon: Number(lng) } : null,
    text_report: packet.injury ? String(packet.injury) : "",
    language: "en",
    vitals: {},
    sensor_flags: {
      auto_generated: Boolean(packet.isAutoGenerated),
      demo: Boolean(packet.isDemo),
      test_data: Boolean(packet.isTestData),
    },
    severity_hint: packet.urgency || "UNKNOWN",
    connectivity: true,
  };
}

/**
 * POST /v1/triage — returns OrchestrationResult JSON from the OmniMesh AI backend.
 */
export async function triageViaBackend(packet) {
  const response = await fetch(`${getBackendUrl()}/v1/triage`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(webPacketToTriagePacket(packet)),
  });

  if (!response.ok) {
    const detail = await response.text();
    throw new Error(`Backend triage failed (${response.status}): ${detail.slice(0, 240)}`);
  }

  return response.json();
}

/** Flip the backend's global connectivity switch for live hybrid-routing demos. */
export async function setBackendConnectivity(online) {
  const state = online ? "on" : "off";
  const response = await fetch(`${getBackendUrl()}/v1/connectivity/${state}`, {
    method: "POST",
  });

  if (!response.ok) {
    const detail = await response.text();
    throw new Error(`Connectivity toggle failed (${response.status}): ${detail.slice(0, 240)}`);
  }

  return response.json();
}

export async function fetchBackendHealth() {
  const response = await fetch(`${getBackendUrl()}/health`);
  if (!response.ok) {
    throw new Error(`Backend health check failed (${response.status})`);
  }
  return response.json();
}

function pickLeadPacket(packets) {
  const list = Array.isArray(packets) ? packets : [];
  if (!list.length) return null;
  const priority = { RED: 0, YELLOW: 1, GREEN: 2 };
  return [...list].sort((a, b) => {
    const u = (priority[a.urgency] ?? 3) - (priority[b.urgency] ?? 3);
    if (u !== 0) return u;
    return (Number(b.createdAt) || 0) - (Number(a.createdAt) || 0);
  })[0];
}

/** Map OrchestrationResult into the dispatch analysis shape the dashboard already renders. */
export function mapBackendResultToAnalysis(orchestrationResult, packets) {
  const fallback = buildDispatchFallback(packets);
  const mode = orchestrationResult?.mode || "local";
  const severity = orchestrationResult?.final_severity || "UNKNOWN";
  const summary =
    orchestrationResult?.dispatch_summary ||
    fallback.analysis ||
    "OmniMesh AI backend triage complete.";

  const modeLabel =
    mode === "hybrid"
      ? "HYBRID — local edge triage + Gemma cloud reconcile"
      : mode === "cloud"
        ? "CLOUD — Gemma via Fireworks AI"
        : "LOCAL — edge heuristic (offline-safe)";

  return {
    ...fallback,
    analysis: `${summary}\n\n[Routing mode: ${modeLabel}]`,
    critical_alert:
      severity === "RED"
        ? summary
        : fallback.critical_alert,
    mode_used: mode,
    backend_result: orchestrationResult,
    dispatch_engine: "backend",
  };
}

/** Triage the highest-priority packet and shape a command-center analysis payload. */
export async function runBackendDispatch(packets) {
  const lead = pickLeadPacket(packets);
  if (!lead) {
    throw new Error("No packets available for backend triage.");
  }
  const orchestration = await triageViaBackend(lead);
  return mapBackendResultToAnalysis(orchestration, packets);
}
